import { CalendarDays, Plus, Trash2 } from "lucide-react";
import { DiscordChannel, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (v: GuildConfig["daily"]) => void;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export function DailyTab({ config, channels, onChange }: Props) {
  const cfg = config.daily;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });

  return (
    <Section
      icon={CalendarDays}
      title="Daily messages"
      description="Sends a message to the chosen channel every day at the selected hour."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>No slash commands.</strong> The bot posts one of your messages every day at the selected hour
          (server time depends on host). Use <strong>Random</strong> or <strong>Sequential</strong> pick mode across
          your message list.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Channel">
          <ChannelPicker
            channels={channels}
            value={cfg.channel_id}
            onChange={(v) => update("channel_id", v === "__none__" ? "" : v)}
          />
        </Field>
        <Field label="Send time">
          <div className="flex items-center gap-2">
            <Select
              value={String(cfg.hour ?? 10)}
              onValueChange={(v) => update("hour", Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS.map((h) => (
                  <SelectItem key={h} value={String(h)}>
                    {String(h).padStart(2, "0")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-muted-foreground">:</span>
            <Select
              value={String(cfg.minute ?? 0)}
              onValueChange={(v) => update("minute", Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {MINUTES.map((m) => (
                  <SelectItem key={m} value={String(m)}>
                    {String(m).padStart(2, "0")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Field>
      </div>

      <Field
        label="Pick mode"
        hint="Random = pick from the list at random; Sequential = rotate in order."
      >
        <div className="flex items-center gap-3">
          <Switch
            checked={!!cfg.random_pick}
            onCheckedChange={(v) => update("random_pick", v)}
          />
          <span className="text-sm">
            {cfg.random_pick ? "Random" : "Sequential"}
          </span>
        </div>
      </Field>

      <div className="space-y-2 pt-2 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Messages</h3>
            <p className="text-xs text-muted-foreground">
              Empty = use built-in default greetings.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => update("messages", [...(cfg.messages || []), ""])}
          >
            <Plus className="h-4 w-4" />
            Add message
          </Button>
        </div>

        <div className="space-y-2">
          {(cfg.messages || []).map((msg, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-xs text-muted-foreground pt-3 w-6 text-center">
                {i + 1}.
              </span>
              <Textarea
                value={msg}
                rows={2}
                onChange={(e) => {
                  const m = [...(cfg.messages || [])];
                  m[i] = e.target.value;
                  update("messages", m);
                }}
                placeholder="Good morning! 🌅"
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() =>
                  update(
                    "messages",
                    (cfg.messages || []).filter((_, j) => j !== i),
                  )
                }
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}
